import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpDownloadProgressEvent,
  HttpErrorResponse,
  HttpEventType,
  HttpResponse
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { ChatRefusedReason, ChatStreamEvent, ChatStreamRefusedEvent } from './chat-stream-event.model';
import { ConversationSummary } from './conversation-summary.model';
import { parseSseEvents } from './sse-event-parser';

const MOCK_CONVERSATIONS: ConversationSummary[] = [
  { id: 'mock-1', title: 'Order status for PO-10482', updatedAt: '2026-06-09T14:32:00Z' },
  { id: 'mock-2', title: 'Restock suggestions for Q3', updatedAt: '2026-06-08T09:15:00Z' },
  { id: 'mock-3', title: 'Delivery time to Oslo warehouse', updatedAt: '2026-06-04T16:48:00Z' },
  { id: 'mock-4', title: 'Alternatives for discontinued 274-115', updatedAt: '2026-05-28T11:02:00Z' },
  { id: 'mock-5', title: 'Copy of invoice 88231', updatedAt: '2026-05-20T08:27:00Z' }
];

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private http = inject(HttpClient);

  getConversations(selectedCustomerId?: string): Observable<ConversationSummary[]> {
    // TODO: backend endpoint not available yet — swap the mock for:
    // return this.http.get<ConversationSummary[]>('json/glippy-ai-chat/conversations', {
    //   params: selectedCustomerId ? { selectedCustomerId } : {}
    // });
    return of(MOCK_CONVERSATIONS);
  }

  stream(conversationId: string, message: string, selectedCustomerId?: string): Observable<ChatStreamEvent[]> {
    return this.http
      .post('json/glippy-ai-chat/stream', { conversationId, message, selectedCustomerId }, {
        observe: 'events',
        responseType: 'text',
        reportProgress: true
      })
      .pipe(
        filter(
          event =>
            event.type === HttpEventType.DownloadProgress ||
            event.type === HttpEventType.Response
        ),
        map(event => {
          if (event.type === HttpEventType.DownloadProgress) {
            return parseSseEvents((event as HttpDownloadProgressEvent).partialText ?? '');
          }
          return parseSseEvents((event as HttpResponse<string>).body ?? '');
        }),
        catchError(error => {
          const refused = refusedEventFromHttpError(error);
          return refused ? of([refused]) : throwError(() => error);
        })
      );
  }
}

/**
 * The backend answers 403 (Glippy disabled) or 429 (budget used up) with `{"type":"refused","reason":...}`
 * before any stream opens. Surface that as the same `refused` event the stream would carry so the chat
 * renders one sentence either way.
 */
export function refusedEventFromHttpError(error: unknown): ChatStreamRefusedEvent | undefined {
  if (!(error instanceof HttpErrorResponse) || (error.status !== 403 && error.status !== 429)) {
    return undefined;
  }
  const body = parseErrorBody(error.error);
  const reason: ChatRefusedReason =
    body?.reason === 'budget' || body?.reason === 'disabled' ? body.reason : error.status === 429 ? 'budget' : 'disabled';
  return { type: 'refused', reason };
}

function parseErrorBody(body: unknown): { type?: string; reason?: string } | undefined {
  // responseType 'text' hands the error body over as a string; the testing controller and interceptors
  // may hand over the parsed object instead.
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return undefined;
    }
  }
  return typeof body === 'object' && body !== null ? (body as { type?: string; reason?: string }) : undefined;
}
